import * as React from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Clock, FileSignature, HelpCircle, Mail, Megaphone, ReceiptText, Wrench } from 'lucide-react';
import { officeNextDayStart, systemClock } from '@reos/config';
import { V4Group, V4Progress, V4Row, V4Section } from '../components/ux-v4.js';
import { PageHead } from '../components/page-head.js';
import { PropertyImage } from '../components/PropertyImage.js';
import { GuideCard } from '../components/guide-card.js';
import { useSession } from '../stores/session.js';
import { saveMyName } from '../lib/api.js';
import { needsRealName } from '../lib/user-name.js';
import { canSeeMenuItem } from '../lib/capabilities.js';
import { getBillingOverview } from '../lib/billing-api.js';
import { listEntitlements } from '../lib/orders-api.js';
import { listCalendarEvents } from '../lib/calendar-api.js';
import { listTodayTasks, transitionTask, type TodayTask } from '../lib/tasks-api.js';
import { decideMcpApproval, listMcpApprovals } from '../lib/mcp-approvals-api.js';
import { getResolveUnknownStats } from '../lib/resolve-unknown-api.js';
import { getMailboxCards, MAILBOX_CARDS_QUERY_KEY } from '../lib/mailbox-api.js';
import { humanToolLabel } from '../lib/mcp-tool-labels.js';
import { downloadContractPdf, listContracts, type ContractDto } from '../lib/contracts-api.js';
import { documentFileName } from '../lib/contract-doc.js';
import { listInvoices, markInvoicePaid, type InvoiceDto } from '../lib/invoices-api.js';
import { getCase, listCases, transitionCase, type KanbanCase } from '../lib/cases-api.js';
import { caseDisplayName } from '../lib/case-title.js';
import {
  CANCELLED_STAGE,
  CASE_TYPE_LABELS,
  KANBAN_STAGE_ORDER,
  stageLabel,
  type KanbanCaseType,
} from '../lib/case-stages.js';
import { caseProgressView } from '../lib/case-progress.js';
import { loadCaseCovers } from '../lib/case-covers.js';
import { formatFullDate, formatRelativeChange, isSameDay } from '../lib/relative-time.js';
import {
  buildBrokerBriefing,
  buildTodayEntries,
  emailCardsOnYou,
  isCaseInMotion,
  membershipEndingSoon,
  nowLineIndex,
  splitTodayEntries,
  type BriefingItem,
  type BriefingSectionKey,
  type TodayEntry,
} from '../lib/dnesek-model.js';
import { dayPart } from '../lib/greeting.js';
import { firstName, formatTime, vocativeCs } from '@reos/i18n';
import {
  AWAITING_SIGNATURE_QUERY_KEY,
  ISSUED_INVOICES_QUERY_KEY,
  LISTING_DEAL_OFFERS_QUERY_KEY,
  SIGNED_CONTRACTS_QUERY_KEY,
  overdueInvoices,
  startOfToday,
  todayTasksQueryKey,
} from '../lib/waiting-for-me.js';
// Krok LIST1: nabidka „Označit rezervované" / „Prodáno" po podpisu smlouvy.
import {
  confirmCaseListingDeal,
  fetchCaseListingOffers,
  type CaseListingDeal,
} from '../lib/connectors.js';
import {
  errorMessage,
  fetchPropertyBriefs,
  kindLabel,
  type PropertyListItem,
} from '../lib/properties.js';
import type { CoverInfo } from '../lib/case-rows.js';
import { useLocale, useT } from '../lib/i18n.js';
import { NewDemandMatches, TodayViewings } from '../components/dnesek-crm4.js';
import { Novinky } from '../components/dnesek-novinky.js';
import { OverviewBoard } from '../components/overview-board.js';

/**
 * Krok SIGN1 bod 4 — KTEROU FAZI ZAKAZKY PO PODPISU NABIDNOUT.
 *
 * Posun se jen NABIZI (podpis ho nedela sam). Cil je prvni DOPREDNA faze,
 * kterou server oznacil jako povolenou (`allowedTransitions`) — stavovy
 * automat se tu neduplikuje, poradi sloupcu kanbanu slouzi jen k tomu, aby
 * se nenabidlo zruseni nebo krok zpet. `null` = neni co nabidnout.
 */
function stageToOffer(item: KanbanCase | undefined): string | null {
  if (item === undefined) return null;
  const allowed: readonly string[] = item.allowedTransitions ?? [];
  const order: readonly string[] = KANBAN_STAGE_ORDER;
  const currentIndex = order.indexOf(item.stage);
  if (currentIndex < 0) return null;
  return order.find((stage, index) => index > currentIndex && allowed.includes(stage)) ?? null;
}

/** ADM1 bod 4: pozadavky starsi nez 14 dni nabidnout k zahozeni misto k rozhodnuti. */
const OLD_APPROVAL_THRESHOLD_MS = 14 * 24 * 60 * 60 * 1000;

/** Kolik zakazek „v pohybu" se vejde do tabulky (predloha `Main.dc.html`: 3). */
const CASES_IN_MOTION = 4;

/** Vysledek bloku „v pohybu" vcetne toho, CI zakazky to vlastne jsou. */
interface CasesInMotion {
  readonly items: readonly CaseInMotion[];
  readonly scope: 'mine' | 'office';
  /**
   * Krok PIL1 (vada V1): ma kancelar VUBEC nejakou zakazku? „Nic na vás
   * nečeká" je spravna veta pro zabehnutou kancelar, ale prvni den po
   * registraci nechava makléřku bez dalsiho kroku (snimek `GEN1/1-08`).
   * Pozna se to z TYCHZ dvou seznamu, ktere blok „v pohybu" uz nacetl —
   * zadny dotaz navic.
   */
  readonly anyCase: boolean;
}

interface CaseInMotion {
  readonly id: string;
  readonly name: string;
  readonly nameHint: string | undefined;
  /** Druhy radek — co to je za obchod (Prodej / Pronájem). */
  readonly kindLabel: string | null;
  /** Faze zakazky; procenta i vetu „co chybí" z ni pocita `caseProgressView`. */
  readonly stage: string;
  /** Krok UXV4a bod 3: nahled nemovitosti u kazde zakazky (predloha). */
  readonly propertyId: string | null;
  readonly coverUrl: string | null;
  readonly lat: number | null;
  readonly lon: number | null;
  readonly changedAt: string;
}

/** Jedna vec, kterou bez uzivatele nikdo neposune (predloha „Na tobě"). */
interface OnYouRow {
  readonly id: string;
  readonly icon: React.ReactNode;
  readonly title: string;
  readonly detail: string;
  /** Prvni tlacitko = JEDINA primarni akce polozky (zadani UXR3 bod 4). */
  readonly actionLabel: string;
  readonly onAction: () => void;
  /** Volitelna druha, nenapadna akce. */
  readonly secondary?: { readonly label: string; readonly onAction: () => void };
  /** FIX6 bod 4: ukol po termínu si nese svuj testid i po presunu do „Na tobě". */
  readonly testId?: string;
  /** Krok UXV4a bod 6: zakazka, ke ktere vec patri (fotka a odkaz na karte). */
  readonly caseId?: string | null;
  readonly propertyId?: string | null;
}

function NameAsk({ onSaved }: { readonly onSaved: () => void }): React.JSX.Element {
  const t = useT();
  const setUserName = useSession((s) => s.setUserName);
  const [value, setValue] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [failed, setFailed] = React.useState(false);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    const name = value.trim();
    if (name === '' || busy) return;
    setBusy(true);
    setFailed(false);
    try {
      setUserName(await saveMyName(name));
      onSaved();
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rh-v4-group rh-v4-card" data-testid="dnesek-name-ask">
      <form onSubmit={(event) => void submit(event)}>
        <h2 className="rh-v4-h2">{t('webAdmin.dnesek.nameAskTitle')}</h2>
        <p className="rh-a2-card__hint">{t('webAdmin.dnesek.nameAskNote')}</p>
        <label className="rh-v4-form-row">
          <span className="rh-v4-form-row__label">{t('webAdmin.dnesek.nameAskLabel')}</span>
          <input
            className="rh-input"
            value={value}
            onChange={(event) => { setValue(event.target.value); }}
            data-testid="dnesek-name-ask-input"
          />
        </label>
        {failed ? (
          <p role="alert" className="rh-a2-error" data-testid="dnesek-name-ask-error">
            {t('webAdmin.dnesek.nameAskFailed')}
          </p>
        ) : null}
        <button
          type="submit"
          className="rh-v4-btn rh-v4-btn--filled"
          disabled={busy || value.trim() === ''}
          data-testid="dnesek-name-ask-save"
        >
          {t('webAdmin.dnesek.nameAskSave')}
        </button>
      </form>
    </section>
  );
}

/**
 * `/prehled` — Dnesek podle SCHVALENE predlohy UX v4 „jednoduse"
 * (`docs/design/ux-v4/png/Main.png`, R.B. 4. 10. 2026 06:11).
 *
 * Obrazovka ma ctyri bloky v poradi predlohy:
 *  1. **velky nadpis** — datum malym nad nim, pod nim pozdrav,
 *  2. **karta „Další krok"** s fotkou nemovitosti a JEDNIM hlavnim
 *     tlacitkem (predloha § Pravidla 2: „jedna obrazovka = jedna prace =
 *     jedno hlavni tlacitko"),
 *  3. **„Čeká na tebe N"** — sbalene, rozbali se na miste (§ Pravidla 1,
 *     postupne odkryvani),
 *  4. **„Dnes"** (program) a **„Zakázky v pohybu"** s nahledem nemovitosti
 *     a progress barem (§ Pravidla 3 a 5).
 *
 * DATA JSOU TATAZ, JAKA MEL DNESEK V UX v3 (krok UXR5) — zadny novy zdroj:
 * kalendar, ukoly dne, smlouvy k podpisu, faktury po splatnosti, schvaleni
 * asistenta, „K doplnění" a zakazky v pohybu. Nove se k nim cte uz jen
 * davkovy `/v1/properties/covers` (nahledy) — tentyz endpoint jako seznam
 * zakazek.
 *
 * Procenta, veta „co chybí" i kroky pochazeji z JEDNE funkce
 * (`lib/case-progress.ts`), kterou vola i detail zakazky — proto na obou
 * obrazovkach stoji stejna cisla.
 */
export function DnesekPage(): React.JSX.Element {
  const t = useT();
  const locale = useLocale();
  const user = useSession((s) => s.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const roles = user?.roles ?? [];
  const canSeeApprovals = canSeeMenuItem(roles, 'workflow.approval.decide');
  const [actionError, setActionError] = React.useState<string | null>(null);
  /*
   * Krok UXV4a bod 6: „Čeká na tebe" bylo SBALENE, protoze prvni (nejdulezitejsi)
   * polozka stala nad nim jako karta „Další krok".
   *
   * Krok GRAF2 (sloucení s Přehledem): kartu kresli tabule karet, ale seznam
   * uz z nej prvni polozku NEVYJIMA — sbaleny oddil by proto schoval i to, co
   * do GRAF2 bylo vzdy videt. Vychozi stav je tedy ROZBALENO; sbalit jde dal.
   */
  const [waitingOpen, setWaitingOpen] = React.useState(true);
  /**
   * Krok PIL1 (vada V2): jmeno uz jsme polozili — karta zmizi hned, bez
   * cekani na odpoved serveru o dalsim nacteni session.
   */
  const [nameAsked, setNameAsked] = React.useState(false);
  const tenant = useSession((s) => s.tenant);
  const askName = !nameAsked && user !== null && needsRealName(user.name, tenant?.name ?? null);

  // Jedno „ted" pro celou obrazovku (clock z @reos/config, ne new Date()).
  const [now] = React.useState(() => systemClock.now());
  /*
   * Krok UXP4a bod 5 (nalez P2-18): okno „dnes" je `Europe/Prague`, stejne
   * jako hranice dnesnich UKOLU na serveru (`officeDayStart` v @reos/config).
   * Driv se konec dne skladal z lokalniho casu prohlizece, takze se okno
   * schuzek a okno ukolu mohlo rozejit o cely den.
   */
  const todayStart = startOfToday(now);
  const todayEnd = new Date(officeNextDayStart(now).getTime() - 1);

  const todayMeetings = useQuery({
    queryKey: ['dnesek', 'calendar-events', todayStart.toDateString()],
    queryFn: () =>
      listCalendarEvents({
        from: todayStart.toISOString(),
        to: todayEnd.toISOString(),
        ...(user !== null ? { ownerUserId: user.id } : {}),
      }),
    enabled: user !== null,
  });

  /*
   * Krok TAB1 bod 1: klic je SPOLECNY s odznakem „Úkoly" v menu
   * (`lib/waiting-for-me.ts`) — menu a Dnesek proto stahuji dnesni ukoly
   * jednou a pocet po termínu pocitaji z tychz dat (`overdueTaskCount`).
   */
  const todayTasks = useQuery({
    queryKey: todayTasksQueryKey(user?.id ?? null),
    queryFn: () => listTodayTasks({ ...(user !== null ? { ownerUserId: user.id } : {}) }),
    enabled: user !== null,
  });

  /*
   * Stejne klice jako panel menu (`lib/waiting-for-me.ts`) — pocty u polozek,
   * u zvonku a tady musi rikat totez a stahovat se jen jednou.
   *
   * KROK SML1 — PROC TU NENI `contractStandings` (TESTOVANI §2, zduvodneni
   * u kodu): odznak „Smlouvy" v menu je pocet smluv „Na tobě", tedy VCETNE
   * rozpracovanych konceptu. „Čeká na mě" na Dnesku slibuje uzsi vec —
   * vetou „Smlouva čeká na podpis" a akci „Podepsat smlouvu" (schvalena
   * predloha UXV4a a mereni DEMO2 bodu 1). Dnesek proto zustava na stavu
   * `awaiting_signature`; az se jeho veta zmeni na „co je na tobě", vezme
   * si tutez funkci jako prehled a menu.
   */
  const pendingContracts = useQuery({
    queryKey: AWAITING_SIGNATURE_QUERY_KEY,
    queryFn: () => listContracts({ status: 'awaiting_signature' }),
    enabled: user !== null,
  });
  /*
   * Krok SIGN1 bod 3 — PODEPSANA SMLOUVA UZ NENI „k podpisu".
   *
   * Podpis posledni strany prepne smlouvu do `signed` (dokonceni podpisu v
   * @reos/documents), takze z dotazu vyse zmizi. Na Dnesku ale ten den jeste
   * neco na makléři je: stahnout podepsanou smlouvu (a muze nabidnout posun
   * faze zakazky). Bere se jen to, co se podepsalo DNES — starsi podepsane
   * smlouvy na „Na tobě" nepatri, ty uz jsou hotove.
   */
  const signedContracts = useQuery({
    queryKey: SIGNED_CONTRACTS_QUERY_KEY,
    queryFn: () => listContracts({ status: 'signed' }),
    enabled: user !== null,
  });
  const signedToday = (signedContracts.data ?? []).filter(
    (contract: ContractDto) =>
      contract.status === 'signed' && isSameDay(new Date(contract.updatedAt), now),
  );
  /*
   * Zakazky podepsanych smluv — kvuli nabidce posunu faze (bod 4). Faze a
   * povolene prechody rika server, UI je neodvozuje z poradi sloupcu kanbanu.
   */
  const signedCaseIds = signedToday
    .map((contract: ContractDto) => contract.caseId)
    .filter((id: string | null): id is string => id !== null);
  const signedCases = useQuery({
    queryKey: ['dnesek', 'signed-contract-cases', signedCaseIds.join(',')],
    enabled: signedCaseIds.length > 0,
    queryFn: async (): Promise<ReadonlyMap<string, KanbanCase>> => {
      const pairs = await Promise.all(
        signedCaseIds.map(async (id): Promise<readonly [string, KanbanCase]> => [id, await getCase(id)]),
      );
      return new Map(pairs);
    },
  });
  /*
   * Krok LIST1 bod 3 — NABIDKA po rezervaci / prodeji. Server rekne, u ktere
   * zakazky je co potvrdit (`/v1/listing-deals/offers`); nic se nestahuje bez
   * kliku makere. Tentyz vypocet stoji za blokem na detailu zakazky.
   */
  const listingDealOffers = useQuery({
    queryKey: LISTING_DEAL_OFFERS_QUERY_KEY,
    queryFn: fetchCaseListingOffers,
    enabled: user !== null,
  });

  const issuedInvoices = useQuery({
    queryKey: ISSUED_INVOICES_QUERY_KEY,
    queryFn: () => listInvoices({ status: 'issued' }),
    enabled: user !== null,
  });

  const approvals = useQuery({
    queryKey: ['dnesek', 'approvals'],
    queryFn: () => listMcpApprovals('pending'),
    enabled: user !== null && canSeeApprovals,
  });

  // Krok UXA1: „K doplnění" uz neni polozka menu — pocitadlo tady je jeho
  // vstupni bod. Stejny endpoint jako stranka `/k-doplneni`, aby cislo
  // nerikalo dve ruzne veci na dvou obrazovkach.
  const resolveUnknown = useQuery({
    queryKey: ['dnesek', 'resolve-unknown-stats'],
    queryFn: getResolveUnknownStats,
    enabled: user !== null,
  });

  // Krok AS1 bod 6: karty „Nové z e-mailu" — tatáž cesta jako tabule Úkoly
  // a MCP `reos.today`; počet dělá `emailCardsOnYou` (@reos/today-metrics).
  // Bez připojené schránky server vrátí prázdný seznam a řádek se neukáže.
  const mailCards = useQuery({
    queryKey: MAILBOX_CARDS_QUERY_KEY,
    queryFn: getMailboxCards,
    enabled: user !== null,
  });

  // Krok FAK1 bod 4 / DEMO2: „Fakturace je připravena na X %" — predloha
  // kartu nezna (ilustrativni kancelar ma fakturaci hotovou), ale
  // nenakonfigurovana instance je skutecny stav, ktery makler musi videt.
  // Ukaze se JEN kdyz je co dodelat.
  const billing = useQuery({
    queryKey: ['dnesek', 'billing-overview'],
    queryFn: () => getBillingOverview(),
    enabled: user !== null,
  });
  const showBillingCard =
    billing.data !== undefined && billing.data.status !== 'unconfigured' && !billing.data.readiness.complete;

  /**
   * Krok D7 bod 3 (R27/R23): clenstvi se plati MESIC PREDEM a bez automatickeho
   * strhavani, takze konec obdobi je vec k zarizeni. Kdy obdobi konci, rika
   * JEDNA funkce (`membershipEndingSoon` v `@reos/today-metrics`) nad naroky
   * z `GET /v1/entitlements` — Dnesek zadne datum nepocita sam.
   */
  const entitlements = useQuery({
    queryKey: ['dnesek', 'entitlements'],
    queryFn: () => listEntitlements(),
    enabled: user !== null,
  });
  const membershipEnding = membershipEndingSoon(entitlements.data ?? [], now);
  const membershipEndingTitle =
    membershipEnding === null
      ? ''
      : t('webAdmin.dnesek.membershipEndingTitle', {
          date: new Intl.DateTimeFormat(locale, { dateStyle: 'medium' }).format(
            new Date(membershipEnding.expiresAt),
          ),
        });

  const casesInMotion = useQuery({
    queryKey: ['dnesek', 'cases-in-motion', user?.id ?? null],
    enabled: user !== null,
    queryFn: async (): Promise<CasesInMotion> => {
      /*
       * Krok NAB4 (oprava e2e 1): oba seznamy se ctou v poradi POSLEDNI
       * ZMENY (`sort: 'updated_desc'`). `GET /v1/cases` jinak strankuje od
       * NEJSTARSI zalozene zakazky, takze `limit: 50` v DEMO kancelari
       * (2000+ zakazek) vracel padesat let starych radku — mezi nimi nebyla
       * ani jedna zmenena za poslednich 7 dni a blok byl trvale prazdny,
       * i kdyz se v kancelari pracovalo (e2e S1, 19. 10. 2026).
       */
      const own = await listCases({
        ...(user !== null ? { ownerUserId: user.id } : {}),
        limit: 50,
        sort: 'updated_desc',
      });
      /**
       * Krok FIX6 bod 4: ZRUSENA zakazka „v pohybu" neni. Po uklidu demo
       * kancelare od evalovych zakazek byly ctyri NEJNOVEJSI zakazky prave ty
       * zrusene — `/zakazky` je skryva, tady to chybelo.
       *
       * Krok UXR3b bod 3 — nalez R.B. 3. 10. 04:50: dlazdice slibuje „změna
       * za posledních 7 dní" (`webAdmin.dnesek.summaryMotionNote`, predloha
       * `Main.dc.html`), ale vyber byl bez ohledu na stari zmeny — mohla tam
       * skoncit zakazka stara mesice. `isCaseInMotion` je proto SOUCASTI
       * filtru, ne jen popisek vedle cisla.
       */
      const activeRecentOf = (items: readonly KanbanCase[]): KanbanCase[] =>
        items.filter((item) => item.stage !== CANCELLED_STAGE && isCaseInMotion(item.updatedAt, now));
      /**
       * Nalez pri zivem overeni UXR1: prihlaseny ucet (demo.majitel) nevlastni
       * zadnou zakazku, takze blok byl natrvalo prazdny. Kdyz uzivatel zadnou
       * svou (nedavno zmenenou) zakazku nema, ukaze se pohyb CELE KANCELARE a
       * popisek to rekne.
       */
      const ownActive = activeRecentOf(own.items);
      /*
       * Krok PIL1 (vada V1): druhy seznam uz se stahoval, jen se z nej bral
       * pouze pohyb. Ted z nej plyne i `anyCase` — „kancelar nema ani jednu
       * zakazku" je neco jineho nez „nic se nehnulo za 7 dní".
       */
      const officeAll =
        ownActive.length > 0 ? null : await listCases({ limit: 50, sort: 'updated_desc' });
      const office = officeAll === null ? null : activeRecentOf(officeAll.items);
      const anyCase = own.items.length > 0 || (officeAll?.items.length ?? 0) > 0;
      const source = office ?? ownActive;
      const recent = [...source]
        .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
        .slice(0, CASES_IN_MOTION);
      /*
       * Krok UXR5 bod 3: druhy radek nese TYP A MISTO nemovitosti („Pozemek ·
       * Lysá nad Labem", predloha `Main.dc.html`), ne druh obchodu — ten byl
       * u vsech ctyr radku stejny („Prodej"). Jedno strankovane volani
       * `fetchPropertyBriefs` da OBOJI: adresu pro zakazky bez pouzitelneho
       * nazvu (krok O8) i typ a obec pro vsechny.
       */
      const needProperty = recent.map((c) => c.propertyId).filter((id): id is string => id !== null);
      const [briefs, covers] = await Promise.all([
        needProperty.length > 0
          ? fetchPropertyBriefs(needProperty).catch(() => new Map<string, PropertyListItem>())
          : Promise.resolve(new Map<string, PropertyListItem>()),
        /*
         * Krok UXV4a bod 3: u kazde zakazky v pohybu ma byt NAHLED NEMOVITOSTI
         * (predloha `Main.dc.html`). Jde o tytez davkove `/v1/properties/covers`,
         * jake pouziva seznam zakazek (`lib/case-covers.ts`) — jeden dotaz na
         * cely blok, ne jeden na radek.
         */
        loadCaseCovers(needProperty).catch(() => new Map<string, CoverInfo>()),
      ]);
      const nameTexts = {
        untitled: t('webAdmin.kanban.untitled'),
        untitledHint: (id: string) => t('webAdmin.kanban.untitledHint', { id }),
      };
      const addresses = new Map(
        [...briefs].filter(([, item]) => item.address !== '').map(([id, item]) => [id, item.address]),
      );
      const items = recent.map((item: KanbanCase): CaseInMotion => {
        const display = caseDisplayName(item, addresses, nameTexts);
        const property = item.propertyId !== null ? briefs.get(item.propertyId) : undefined;
        /*
         * „Pozemek · Lysá nad Labem". Kdyz nemovitost neznáme (zakazka bez
         * `propertyId`, nebo se seznam nenacetl), zustava druh obchodu —
         * nic se nevymysli a radek nezustane holy.
         */
        const place = [
          property !== undefined ? kindLabel(property.kind) : null,
          property?.locality ?? null,
        ].filter((part): part is string => part !== null && part !== '');
        const cover = item.propertyId === null ? undefined : covers.get(item.propertyId);
        return {
          id: item.id,
          name: display.text,
          nameHint: display.hint,
          kindLabel:
            place.length > 0 ? place.join(' · ') : (CASE_TYPE_LABELS[item.type as KanbanCaseType] ?? null),
          stage: item.stage,
          propertyId: item.propertyId,
          coverUrl: cover?.thumbUrl ?? null,
          lat: cover?.lat ?? null,
          lon: cover?.lon ?? null,
          changedAt: item.updatedAt,
        };
      });
      return { items, scope: office !== null ? 'office' : 'mine', anyCase };
    },
  });

  /*
   * Krok UXV4a bod 6 — FOTKA NA KARTE „Další krok".
   *
   * Predloha ma na karte obrazek NEMOVITOSTI, ke ktere dalsi krok patri.
   * Smlouva nese `propertyId` sama; kdyz ne (nebo je to faktura, ktera
   * `propertyId` nikdy nema), dohleda se pres zakazku (`getCase`) — VZDY,
   * ne jen kdyz je zakazka mezi poslednimi zmenenymi v bloku „v pohybu".
   *
   * Krok PIL5 (vada): mapa `propertyByCase` z bloku „v pohybu" znala jen
   * hrstku NAPOSLED zmenenych zakazek, takze smlouva zakazky MIMO ni
   * zustavala bez nahledu, i kdyz nemovitost souradnice mela (nalez: smlouva
   * 01a0efbd, zakazka Poděbradova 141/44 — stejny vzor opravy uz pouziva
   * `components/contracts.tsx` u navrhu z dat zakazky).
   *
   * Poradi je TOTEZ jako u `onYouRows` niz (smlouva → faktura), proto se
   * nevyhodnocuje dvakrat ruzne: prvni polozka seznamu je prave tahle.
   */
  /*
   * Krok GRAF2 (sloucení s Přehledem): obalka karty „Další krok" (dva dotazy
   * navic — zakazka a jeji titulni fotka) je PRYC. Nahled i oba zalozni stavy
   * posila souhrn `GET /v1/overview` v jedne odpovedi; tohle byly presne ty
   * dotazy, ktere zadani GRAF2 rusi („jeden dotaz na souhrn, ne 12").
   */

  async function discardApproval(id: string): Promise<void> {
    if (typeof window !== 'undefined' && !window.confirm(t('webAdmin.dnesek.toolDiscardConfirm'))) return;
    setActionError(null);
    try {
      await decideMcpApproval(id, 'rejected', {
        stepUpPrompt: t('webAdmin.approvals.stepUpPrompt'),
        reason: t('webAdmin.dnesek.toolDiscardReason'),
      });
      await queryClient.invalidateQueries({ queryKey: ['dnesek', 'approvals'] });
    } catch (e) {
      setActionError(t('webAdmin.dnesek.toolDiscardError', { error: errorMessage(e) }));
    }
  }

  /**
   * Odskrtnuti „Hotovo" jde pres STEJNY prechod jako pretazeni na tabuli
   * (`transitionTask`) — jinak by se ukol v tabuli musel dohanet rucne.
   */
  async function completeTask(task: TodayTask): Promise<void> {
    setActionError(null);
    try {
      await transitionTask(task.id, 'done');
      await queryClient.invalidateQueries({ queryKey: todayTasksQueryKey(user?.id ?? null) });
    } catch (e) {
      setActionError(errorMessage(e));
    }
  }

  /**
   * Krok SIGN1 bod 3 — „Stáhnout podepsanou smlouvu". Tentyz endpoint, jaky
   * ma tlacitko „Stáhnout PDF" na detailu smlouvy (`downloadContractPdf`),
   * zadna druha cesta k souboru.
   */
  async function downloadSigned(contract: ContractDto): Promise<void> {
    setActionError(null);
    try {
      await downloadContractPdf(contract.id, `${documentFileName(contract.title)}.pdf`);
    } catch (e) {
      setActionError(errorMessage(e));
    }
  }

  /**
   * Krok SIGN1 bod 4 — POSUN FAZE ZAKAZKY JEN NABIDNOUT. Podpis fazi
   * NEPOSOUVA sam; tady je tlacitko, ktere to udela na vyslovne kliknuti.
   * Cilovou fazi rika SERVER (`allowedTransitions` na zakazce), UI stavovy
   * automat neduplikuje.
   */
  async function moveCase(caseId: string, toStage: string): Promise<void> {
    setActionError(null);
    try {
      await transitionCase(caseId, toStage);
      await queryClient.invalidateQueries({ queryKey: SIGNED_CONTRACTS_QUERY_KEY });
      await queryClient.invalidateQueries({ queryKey: ['dnesek', 'signed-contract-cases'] });
      await queryClient.invalidateQueries({ queryKey: ['dnesek', 'cases-in-motion', user?.id ?? null] });
    } catch (e) {
      setActionError(errorMessage(e));
    }
  }

  /**
   * Krok LIST1 — POTVRZENI stavu inzeratu. Jeden klik = jedno odeslani na
   * vsechny zive publikace (stazeni u „Prodáno", `extra_info` u rezervace);
   * stejny endpoint jako blok na detailu zakazky, zadna druha cesta.
   */
  async function confirmListingDeal(
    caseId: string,
    kind: 'RESERVED' | 'SOLD',
    mode?: 'WITHDRAW' | 'KEEP_SOLD',
  ): Promise<void> {
    setActionError(null);
    try {
      await confirmCaseListingDeal(caseId, { kind, ...(mode === undefined ? {} : { mode }) });
      await queryClient.invalidateQueries({ queryKey: LISTING_DEAL_OFFERS_QUERY_KEY });
    } catch (e) {
      setActionError(errorMessage(e));
    }
  }

  /**
   * Krok UXR5 bod 2 — druha akce karty faktury (predloha: „Označit
   * zaplaceno"). Je to ZMENA STAVU DOKLADU, proto s potvrzenim; `markInvoicePaid`
   * je tentyz endpoint, jaky pouziva seznam dokladu ve Fakturaci.
   */
  async function markPaid(invoice: InvoiceDto): Promise<void> {
    if (typeof window !== 'undefined' && !window.confirm(t('webAdmin.dnesek.markPaidConfirm'))) return;
    setActionError(null);
    try {
      await markInvoicePaid(invoice.id);
      await queryClient.invalidateQueries({ queryKey: ISSUED_INVOICES_QUERY_KEY });
    } catch (e) {
      setActionError(errorMessage(e));
    }
  }

  if (!user) {
    return (
      <div className="rh-a2-page">
        <h1 className="rh-a2-pagehead__title">{t('webAdmin.dnesek.heading')}</h1>
        <p>
          {t('webAdmin.dnesek.loginPromptPrefix')}{' '}
          <Link to="/login" className="rh-a2-link">
            {t('webAdmin.dnesek.loginLinkText')}
          </Link>
          .
        </p>
      </div>
    );
  }

  const tasksById = new Map((todayTasks.data ?? []).map((task) => [task.id, task]));
  const allEntries = buildTodayEntries(
    (todayMeetings.data ?? []).map((event) => ({
      id: event.id,
      title: event.title,
      startsAt: event.startsAt,
      location: event.location,
      // Krok UXR5 bod 1: typ rozhoduje o akci, barvu tecky posila server
      // (tataz barva jako v kalendari — `calendar-event-types.ts`).
      eventType: event.eventType,
      color: event.color,
      caseId: event.caseId,
    })),
    (todayTasks.data ?? []).map((task) => ({
      id: task.id,
      title: task.title,
      dueAt: task.dueAt,
      overdue: task.overdue,
      status: task.status,
      detail:
        task.originKind !== null && task.originLabel !== null && task.originLabel !== ''
          ? task.originLabel
          : task.description,
    })),
  );

  /*
    Krok FIX6 bod 4: program je JEN dnesek. Ukoly s vcerejsim terminem
    (`/v1/crm/tasks/today` je posila taky) patri do „Na tobě".
  */
  const { today: entries, overdue: overdueEntries } = splitTodayEntries(allEntries);
  const nowAt = nowLineIndex(entries, now);

  const overdue = overdueInvoices(issuedInvoices.data ?? [], now);
  /*
   * Pocty, ze kterych se sklada seznam „Čeká na tebe" (a jeho odznak).
   * `tasks` je PRVNI pole (stejne poradi jako v `onYouRows`).
   *
   * Krok GRAF4 nalez 1: VETU STAVU uz z nich nikdo nesklada — `buildLede`
   * tu byl az do GRAF4 a hlasil pod nadpisem „Dnes na vás nic nehoří.", i
   * kdyz korálový pruh „Co hoří" ze souhrnu `GET /v1/overview` hlasil dve
   * faktury a smlouvu. Funkce zustava v @reos/today-metrics pro rannni
   * briefing a MCP `reos.today`; na Prehledu je ale JEDEN hlas, a ten
   * patri pruhu.
   */
  const counts = {
    tasks: overdueEntries.length,
    contracts: pendingContracts.data?.length ?? 0,
    invoices: overdue.length,
    approvals: approvals.data?.length ?? 0,
    toResolve: resolveUnknown.data?.tasksOpen ?? 0,
    emails: emailCardsOnYou(mailCards.data ?? []).length,
    listings: listingDealOffers.data?.length ?? 0,
  };

  const relativeTexts = {
    today: (time: string) => t('webAdmin.relativeTime.today', { time }),
    yesterday: (time: string) => t('webAdmin.relativeTime.yesterday', { time }),
  };

  /**
   * Krok UI5 bod 1 — RANNÍ BRIEFING (`buildBrokerBriefing`, @reos/today-metrics,
   * krok AI9): sekce „Co hoří" a „Koho zavolat" ze stejných dat, která Dnešek
   * uz nacetl (overdueEntries, overdue faktury, e-mailové karty). Prázdná
   * sekce se vubec nevykresli.
   *
   * ROZHODNUTÍ (UI5): sekce „viewings"/"tasks" jsou tady schválně PRÁZDNÉ —
   * „Dnešní prohlídky" (TodayViewings) a program dne níž na téže obrazovce uz
   * tytéž věci ukazují; druhé vykreslení by bylo zmatečné duplikátem.
   */
  const briefing = buildBrokerBriefing({
    /*
     * Krok GRAF2 (sloucení s Přehledem): oddil „Co hoří" uz NENI tady —
     * kresli ho STEJNOJMENNA karta v tabuli karet ze souhrnu
     * `GET /v1/overview`. Dva nadpisy „Co hoří" na jedne obrazovce (predloha
     * ma jeden) by byly druha implementace teze veci. Oddil „Koho zavolat"
     * zustava: zadna karta predlohy ho nenese.
     */
    fires: [],
    toCall: emailCardsOnYou(mailCards.data ?? []).map((card): BriefingItem => ({
      id: card.id,
      text: t('webAdmin.dnesek.briefing.toCallRow', {
        who: card.fromName ?? card.fromEmail ?? t('webAdmin.dnesek.briefing.toCallUnknown'),
      }),
      caseId: card.caseId,
    })),
    viewings: [],
    tasks: [],
  });
  const briefingLabels: Record<BriefingSectionKey, string> = {
    fires: t('webAdmin.dnesek.briefing.fires'),
    to_call: t('webAdmin.dnesek.briefing.toCall'),
    viewings: t('webAdmin.dnesek.briefing.viewings'),
    tasks: t('webAdmin.dnesek.briefing.tasks'),
  };

  /*
   * PORADI JE SOUCAST ZADANI (UXR5 bod 2, plati dal): podpis smlouvy >
   * faktura po splatnosti > ukol po termínu > „K doplnění" > schvaleni.
   *
   * Krok UXV4a bod 6: PRVNI polozka je „Další krok" (karta s fotkou,
   * predloha `Main.png`), zbytek je za rozbalenim „Čeká na tebe N". Je to
   * tentyz seznam a totez poradi — jen jinak vykreslene.
   */
  const onYouRows: readonly OnYouRow[] = [
    /*
     * Krok SIGN1 bod 3: „Podepsat smlouvu" smi nabidnout JEN smlouva, ktera
     * na podpis opravdu ceka. Podepsana (`signed`) ma radek niz, s akci
     * „Stáhnout podepsanou smlouvu" — nalez auditu 14. 10. byl prave tenhle:
     * plne podepsana smlouva dal zvala k podpisu.
     */
    ...(pendingContracts.data ?? [])
      .filter((contract: ContractDto) => contract.status === 'awaiting_signature')
      .map((contract: ContractDto): OnYouRow => ({
        id: `contract-${contract.id}`,
        icon: <FileSignature size={18} strokeWidth={1.9} />,
        title: t('webAdmin.dnesek.contractAwaitingSignatureTitle'),
        detail: contract.title,
        actionLabel: t('webAdmin.dnesek.signContractLabel'),
        onAction: () => void navigate({ to: '/smlouvy/$id', params: { id: contract.id } }),
        caseId: contract.caseId,
        propertyId: contract.propertyId,
      })),
    ...signedToday.map((contract: ContractDto): OnYouRow => {
      const linkedCase =
        contract.caseId === null ? undefined : signedCases.data?.get(contract.caseId);
      const offeredStage = stageToOffer(linkedCase);
      const caseId = contract.caseId;
      return {
        id: `contract-signed-${contract.id}`,
        icon: <FileSignature size={18} strokeWidth={1.9} />,
        title: t('webAdmin.dnesek.contractSignedTitle'),
        detail: contract.title,
        actionLabel: t('webAdmin.dnesek.downloadSignedContractLabel'),
        onAction: () => void downloadSigned(contract),
        ...(offeredStage !== null && caseId !== null
          ? {
              secondary: {
                label: t('webAdmin.dnesek.moveCaseToLabel', { phase: stageLabel(offeredStage) }),
                onAction: () => void moveCase(caseId, offeredStage),
              },
            }
          : {}),
        caseId: contract.caseId,
        propertyId: contract.propertyId,
        testId: `dnesek-contract-signed-${contract.id}`,
      };
    }),
    /*
     * Krok LIST1 bod 3 — INZERAT PO REZERVACI / PRODEJI. Radek stoji HNED za
     * podepsanou smlouvou, protoze z ni plyne: podepsana rezervace → „Označit
     * rezervované", podepsana kupni/najemni → „Prodáno: stáhnout". Vychozi
     * volba u prodeje je STAZENI (zadani bod 2), druha akce ponecha inzerat
     * jako prodany na N dni. Bez kliku se nestahuje nic.
     */
    ...(listingDealOffers.data ?? [])
      .filter((item: CaseListingDeal) => item.offer !== null)
      .map((item: CaseListingDeal): OnYouRow => {
        const offer = item.offer;
        const portals = offer === null ? '' : offer.portals.join(', ');
        const sold = offer?.kind === 'SOLD';
        return {
          id: `listing-deal-${item.caseId}`,
          icon: <Megaphone size={18} strokeWidth={1.9} />,
          title: t(
            sold ? 'webAdmin.listingDeal.offerSoldTitle' : 'webAdmin.listingDeal.offerReservedTitle',
          ),
          detail: t(
            sold
              ? 'webAdmin.listingDeal.offerSoldDetail'
              : 'webAdmin.listingDeal.offerReservedDetail',
            { portals },
          ),
          actionLabel: t(
            sold
              ? 'webAdmin.listingDeal.offerSoldWithdraw'
              : 'webAdmin.listingDeal.offerReservedConfirm',
          ),
          onAction: () =>
            void confirmListingDeal(item.caseId, sold ? 'SOLD' : 'RESERVED', sold ? 'WITHDRAW' : undefined),
          ...(sold && offer !== null
            ? {
                secondary: {
                  label: t('webAdmin.listingDeal.offerSoldKeep', { days: offer.keepDefaultDays }),
                  onAction: () => void confirmListingDeal(item.caseId, 'SOLD', 'KEEP_SOLD'),
                },
              }
            : {}),
          caseId: item.caseId,
          ...(item.propertyId === null ? {} : { propertyId: item.propertyId }),
          testId: `dnesek-listing-deal-${item.caseId}`,
        };
      }),
    ...overdue.map((invoice: InvoiceDto): OnYouRow => ({
      id: `invoice-${invoice.id}`,
      icon: <ReceiptText size={18} strokeWidth={1.9} />,
      title: t('webAdmin.dnesek.invoiceOverdueTitle'),
      detail: t('webAdmin.dnesek.invoiceOverdueDetail', {
        customer: invoice.customer.name,
        dueDate: new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'medium' }).format(new Date(invoice.dueDate)),
      }),
      /*
       * Predloha ma u faktury DVE akce: „Připomenout klientce" (primarni) a
       * „Označit zaplaceno". Pripomenuti je e-mail klientovi — odesila se
       * tam, kde je videt cely doklad a jeho prijemce (`/fakturace`), ne
       * jednim klikem z prehledu. „Označit zaplaceno" je zmena stavu
       * dokladu, proto s potvrzenim (stejny vzor jako „Zahodit" u schvaleni).
       */
      actionLabel: t('webAdmin.dnesek.remindCustomerLabel'),
      onAction: () => void navigate({ to: '/fakturace' }),
      secondary: {
        label: t('webAdmin.dnesek.markPaidLabel'),
        onAction: () => void markPaid(invoice),
      },
      caseId: invoice.caseId,
    })),
    ...overdueEntries.map((entry): OnYouRow => {
      const task = tasksById.get(entry.id.replace('task-', ''));
      const when =
        entry.at === null
          ? entry.title
          : t('webAdmin.dnesek.taskOverdueDetail', {
              title: entry.title,
              when: formatRelativeChange(entry.at, now, relativeTexts),
            });
      /*
       * Krok CRM2 bod 3 (vraceno v GRAF2t): automaticky vznikly ukol nese
       * ODKAZ NA ZDROJ. `entry.detail` je `origin_label` (nebo popis) —
       * tentyz zdroj, jaky ukazuje karta na tabuli Úkoly. Do slouceni
       * s Přehledem to rikal radek programu dne; souhrn `GET /v1/overview`
       * `origin_label` neposila, takze bez tohohle by zdroj ze Dneška zmizel.
       */
      return {
        id: `overdue-${entry.id}`,
        icon: <Clock size={18} strokeWidth={1.9} />,
        title: t('webAdmin.dnesek.taskOverdueTitle'),
        detail: entry.detail === null || entry.detail === '' ? when : `${when} · ${entry.detail}`,
        actionLabel: t('webAdmin.dnesek.doneAction'),
        onAction:
          task !== undefined ? () => void completeTask(task) : () => void navigate({ to: '/ukoly' }),
        testId: `dnesek-task-overdue-${task?.id ?? entry.id}`,
      };
    }),
    /*
     * Krok AS1 bod 6: e-maily jako JEDEN souhrnný řádek (stejný vzor jako
     * „K doplnění") — karty samotné žijí na tabuli Úkoly, tady je jen
     * vstupní bod. Pořadí: za úkoly po termínu (věta `buildLede` totéž).
     */
    ...(counts.emails > 0
      ? [
          {
            id: 'emails',
            icon: <Mail size={18} strokeWidth={1.9} />,
            title: t('webAdmin.dnesek.emailsTitle'),
            detail: t('webAdmin.dnesek.emailsDetail', { count: counts.emails }),
            actionLabel: t('webAdmin.dnesek.openEmails'),
            onAction: () => void navigate({ to: '/ukoly' }),
            testId: 'dnesek-emails',
          } satisfies OnYouRow,
        ]
      : []),
    ...(counts.toResolve > 0
      ? [
          {
            id: 'resolve-unknown',
            icon: <HelpCircle size={18} strokeWidth={1.9} />,
            title: t('webAdmin.dnesek.resolveUnknownTitle'),
            /*
             * Krok GRAF4t: POCET nese popisek radku (tentyz vzor jako
             * „Nové z e-mailu"). Do GRAF4 ho rikala veta stavu v hlavicce;
             * ta zmizela, cislo `tasksOpen` ale makler potrebuje videt — bez
             * nej je „K doplnění" pocitadlo bez poctu.
             */
            detail: t('webAdmin.dnesek.resolveUnknownDetail', { count: counts.toResolve }),
            actionLabel: t('webAdmin.dnesek.openResolveUnknown'),
            onAction: () => void navigate({ to: '/k-doplneni' }),
            testId: 'dnesek-resolve-unknown',
          } satisfies OnYouRow,
        ]
      : []),
    ...(approvals.data ?? []).map((approval): OnYouRow => {
      const isOld = now.getTime() - new Date(approval.requestedAt).getTime() > OLD_APPROVAL_THRESHOLD_MS;
      return {
        id: `approval-${approval.id}`,
        icon: <Wrench size={18} strokeWidth={1.9} />,
        title: humanToolLabel(approval.tool),
        detail: formatRelativeChange(approval.requestedAt, now, relativeTexts),
        actionLabel: isOld ? t('webAdmin.dnesek.toolDiscardLabel') : t('webAdmin.dnesek.openApprovalsLabel'),
        onAction: isOld ? () => void discardApproval(approval.id) : () => void navigate({ to: '/schvaleni' }),
      };
    }),
  ];
  /*
   * Krok UXV4a bod 6: PRVNI vec je karta „Další krok", ZBYTEK je za
   * rozbalenim „Čeká na tebe N" (predloha `Main.png`). Krati se tou samou
   * funkci jako v UX v3 (`splitOnYou`), jen s jednou kartou misto dvou —
   * odznak u „Čeká na tebe" je proto presne `rest`, ne cely seznam.
   */
  /*
   * Krok GRAF2 (sloucení s Přehledem): prvni polozka uz se NEVYTAHUJE do
   * karty „Další krok" — tu kresli tabule karet ze souhrnu. „Čeká na tebe"
   * proto nese CELY seznam; jinak by nejdulezitejsi vec ze seznamu zmizela.
   */
  const waitingRows = onYouRows;
  /*
   * Zakazky, na kterych lezi smlouva cekajici na podpis — veta „chybí tvůj
   * podpis" u prouzku postupu (predloha `Main.png`). Jsou to TATAZ data, ze
   * kterych vznikl prvni radek „na tobě", ne druhy dotaz.
   */
  const signatureCaseIds = new Set(
    (pendingContracts.data ?? [])
      .map((contract: ContractDto) => contract.caseId)
      .filter((id): id is string => id !== null),
  );

  /*
   * Krok PIL1 (vada V19) — „CO SE DNES STALO". Vecerni Dnesek neukazoval ani
   * podepsanou smlouvu, ani ulozeny nabor; makléřka videla „Nic na tebe
   * nečeká" (snimek `GEN1/7-01`). Oddil se sklada z DAT, KTERA UZ NA
   * OBRAZOVCE JSOU — nic se nedotahuje:
   *   a) polozky dnesniho programu, ktere uz jsou odskrtnute nebo za nami,
   *   b) zakazky, ktere se dnes pohnuly (`updatedAt` je dnesni).
   * Nic se nedomysli: kdyz se nic nestalo, oddil to rekne vetou.
   */
  const happened: readonly {
    readonly id: string;
    readonly title: string;
    readonly detail: string;
    readonly caseId: string | null;
  }[] = [
    ...entries
      .map((entry, index) => ({ entry, past: index < nowAt }))
      .filter(({ entry, past }) => entry.done || past)
      .map(({ entry }) => ({
        id: entry.id,
        title: entry.title,
        detail: entry.done ? t('webAdmin.dnesek.happenedTaskDone') : entryTime(entry),
        caseId: entry.caseId,
      })),
    ...(casesInMotion.data?.items ?? [])
      .filter((item) => isSameDay(new Date(item.changedAt), now))
      .map((item) => ({
        id: `case-${item.id}`,
        title: item.name,
        detail: t('webAdmin.dnesek.happenedCaseChanged'),
        caseId: item.id,
      })),
  ];

  /**
   * Pozdrav podle casu a osloveni v 5. padu — predloha „Dobré dopoledne,
   * Richarde" (krok UXR5 bod 5; do UXR5 tu stalo „Dobré ráno, Richard").
   */
  function greeting(): string {
    // `user` je nad timto bodem uz overeny (early return vyse), ale uvnitr
    // funkce to TypeScript nevi — proto lokalni jmeno, ne `user!`.
    const name = vocativeCs(firstName(user?.name ?? ''));
    switch (dayPart(now.getHours())) {
      case 'morning':
        return t('webAdmin.dnesek.greetingMorning', { name });
      case 'forenoon':
        return t('webAdmin.dnesek.greetingForenoon', { name });
      case 'afternoon':
        return t('webAdmin.dnesek.greetingAfternoon', { name });
      case 'evening':
        return t('webAdmin.dnesek.greetingEvening', { name });
    }
  }

  /**
   * Levy sloupec programu. U dnesni polozky staci cas, ale
   * `/v1/crm/tasks/today` posila i ukoly s VCEREJSIM terminem — u tech by
   * holy cas lhal, ze jsou dnes (nalez pri zivem overeni UXR1).
   */
  function entryTime(entry: TodayEntry): string {
    if (entry.at === null) return t('webAdmin.dnesek.todayByEvening');
    const at = new Date(entry.at);
    if (isSameDay(at, now)) {
      // Krok KAL1: tentyz zapis casu jako agenda kalendare (`formatTime`).
      return formatTime(at, { locale: 'cs-CZ' });
    }
    return formatRelativeChange(entry.at, now, relativeTexts);
  }

  /** Jedno tlacitko polozky — v karte „Další krok" plne, v radku kapsle. */
  function rowAction(row: OnYouRow, variant: 'filled' | 'chip'): React.JSX.Element {
    const className =
      variant === 'filled' ? 'rh-v4-btn rh-v4-btn--filled rh-v4-btn--block' : 'rh-v4-btn rh-v4-btn--chip';
    return (
      <>
        <button type="button" className={className} onClick={row.onAction}>
          {row.actionLabel}
        </button>
        {/*
          Krok GRAF2 (sloucení s Přehledem): druha, nenapadna akce (napr.
          SIGN1 bod 4 „Posunout zakázku do …") se do slouceni kreslila jen
          v karte „Další krok". Ta uz tu neni, takze by nabidka zmizela —
          radek ji proto nese take, jen tise (`--tinted`).
        */}
        {row.secondary !== undefined ? (
          <button
            type="button"
            className={variant === 'filled' ? 'rh-v4-btn rh-v4-btn--tinted rh-v4-btn--block' : 'rh-v4-btn rh-v4-btn--chip'}
            onClick={row.secondary.onAction}
          >
            {row.secondary.label}
          </button>
        ) : null}
      </>
    );
  }

  return (
    <div className="rh-v4-page" data-testid="dnesek-page">
      {/* ---------------------------------- hlavicka s velkym nadpisem (UXK1: PageHead, variant plain) */}
      <PageHead
        variant="plain"
        breadcrumb={formatFullDate(now)}
        title={t('webAdmin.dnesek.heading')}
        titleTestId="dnesek-title"
        subtitle={
          <>
            {/* Pozdrav (UXR5 bod 5: osloveni v 5. pade) je v predloze v4 TRETI
                radek hlavicky, ne nadpis obrazovky. Testid jde s nim. */}
            <div data-testid="dnesek-greeting">{greeting()}</div>
            {/*
              Krok GRAF4 nalez 1 — VETA STAVU UZ POD NADPISEM NENI.
              Dozor nasel na Prehledu rozpor: „Dnes na vás nic nehoří." a hned
              pod tim koralovy pruh „Co hoří: 2 faktury po splatnosti · 1
              smlouva čeká na podpis". Dva vypocty, dva zdroje: veta scitala
              dotazy prohlizece (`buildLede` nad `/v1/contracts`, fakturami,
              ukoly), pruh cte souhrn `GET /v1/overview`. Predloha
              (`maklerov-grafika-v3`) ma pod nadpisem JEN pozdrav; to, co hori,
              rika pruh. Nic se neztratilo — kazda polozka, kterou veta
              jmenovala (smlouvy, faktury, ukoly po termínu, e-maily, inzeraty,
              schvaleni, „K doplnění"), ma dal SVUJ RADEK v „Čeká na tebe" a je
              v jeho odznaku zapocitana (`onYouRows` niz).
            */}
          </>
        }
      />

      {actionError !== null ? (
        <p role="alert" className="rh-a2-error" data-testid="dnesek-task-action-error">
          {actionError}
        </p>
      ) : null}

      {/* Krok PIL1 (vada V2): ucet z aktivace nese nazev firmy — zeptame se
          na jmeno cloveka. Rozhoduje JEDNA funkce (`lib/user-name.ts`). */}
      {askName ? <NameAsk onSaved={() => { setNameAsked(true); }} /> : null}

      {/* Krok D7 bod 3: konec zaplaceneho mesice clenstvi. Nad sloupci, ne
          v nich — je to veta o cele kancelari, ne polozka programu dne. */}
      {membershipEnding !== null ? (
        <V4Group testId="dnesek-membership-ending" label={membershipEndingTitle}>
          <V4Row
            icon={<Clock size={18} strokeWidth={1.9} />}
            title={membershipEndingTitle}
            sub={t('webAdmin.dnesek.membershipEndingDetail', { days: membershipEnding.daysLeft })}
          />
        </V4Group>
      ) : null}

      {/* Krok GRAF2: tabule karet podle schvalene predlohy. Data chodi JEDNIM
          dotazem (`GET /v1/overview`); bloky niz zustavaji do kroku, ktery je
          do karet sloucí (viz REPORT-step-GRAF2 „ZBÝVÁ").

          Krok GRAF6 nalez 2 — TABULE JE PRVNI POD HLAVICKOU. Do GRAF6 nad ni
          stály „Novinky" a briefing („Koho zavolat"), takze na zive kancelari
          zacinaly karty az pod ohybem (montaz `docs/reos/grafika-smer/
          montaz-tmavy.jpg`). Predloha ma pod nadpisem pruh „Co hoří" a hned
          mrizku karet; seznamy, ktere karty nenahrazuji, jdou POD ni. */}
      <OverviewBoard />

      {/* Krok NOTIF1 bod 3: „Novinky" — co se v kanceláři stalo a makléř to
          ještě nečetl. Prázdný blok se nekreslí. Od GRAF6 POD tabuli: je to
          seznam prectenych/neprectenych polozek, ne prvni pohled na den. */}
      <Novinky />

      {/* Krok UI5 bod 1: ranní briefing — „Co hoří" a „Koho zavolat" (prázdné sekce zmizí). */}
      {briefing.length > 0 ? (
        <div data-testid="dnesek-briefing">
          {briefing.map((section) => (
            <React.Fragment key={section.key}>
              <h2 className="rh-v4-h2">{briefingLabels[section.key]}</h2>
              <V4Group testId={`dnesek-briefing-${section.key}`} label={briefingLabels[section.key]}>
                {section.items.map((item) => (
                  <V4Row
                    key={item.id}
                    title={item.text}
                    {...(item.caseId !== null
                      ? {
                          chevron: true,
                          render: (className: string, content: React.ReactNode) => (
                            <Link to="/zakazky/$id" params={{ id: item.caseId as string }} className={className}>
                              {content}
                            </Link>
                          ),
                        }
                      : {})}
                  />
                ))}
              </V4Group>
            </React.Fragment>
          ))}
        </div>
      ) : null}

      <div className="rh-v4-cols">
        <div className="rh-v4-col">
          {/*
            Krok GRAF2 (sloucení s Přehledem): karta „Další krok" uz NENI tady.
            Kresli ji tabule karet (`components/overview-board.tsx`) z JEDINEHO
            dotazu `GET /v1/overview` — vcetne fotky nemovitosti a obou zaloh
            (prvni zakazka, rozpracovana zakazka), ktere do GRAF2 zily tady
            (PIL1 vady V1 a V19). Dve karty tehoz jmena na jedne obrazovce by
            byly druha implementace teze veci.
          */}
          {/* ------------------------- Krok PRU1: „Co si vyzkoušet" (per makléř) */}
          <GuideCard fallbackCaseId={casesInMotion.data?.items[0]?.id ?? null} />

          {/* --------------------------------------- „Čeká na tebe N" */}
          {waitingRows.length > 0 ? (
            <V4Section
              label={t('webAdmin.dnesek.waitingForYouTitle')}
              count={waitingRows.length}
              open={waitingOpen}
              onToggle={() => { setWaitingOpen((open) => !open); }}
              testId="dnesek-waiting-toggle"
            >
              <div data-testid="dnesek-waiting-list">
                {waitingRows.map((row) => (
                  <V4Row
                    key={row.id}
                    {...(row.testId !== undefined ? { testId: row.testId } : {})}
                    title={row.title}
                    sub={row.detail}
                    right={rowAction(row, 'chip')}
                  />
                ))}
              </div>
            </V4Section>
          ) : null}

          {approvals.error != null ? (
            <p role="alert" className="rh-a2-error">
              {t('webAdmin.dnesek.approvalsLoadErrorPrefix', { error: errorMessage(approvals.error) })}
            </p>
          ) : null}
        </div>

        <div className="rh-v4-col">
          {/*
            Krok GRAF2 (sloucení s Přehledem): program dne kresli karta „Dnes"
            v tabuli karet, vcetne odskrtnuti ukolu („Hotovo", tentyz prechod
            `transitionTask`) a odkazu na kalendar a ukoly. Zdroj je souhrn
            `GET /v1/overview`, ne dva vlastni dotazy jako driv.
          */}
          {/* ------------------------------------- „Co se dnes stalo" (V19) */}
          {happened.length > 0 || dayPart(now.getHours()) === 'evening' ? (
            <>
              <h2 className="rh-v4-h2" id="dnesek-happened-h">
                {t('webAdmin.dnesek.happenedHeading')}
              </h2>
              {happened.length > 0 ? (
                <V4Group testId="dnesek-happened-list" label={t('webAdmin.dnesek.happenedHeading')}>
                  {happened.map((row) => (
                    <V4Row
                      key={row.id}
                      testId={`dnesek-happened-${row.id}`}
                      title={row.title}
                      sub={row.detail}
                      {...(row.caseId === null
                        ? {}
                        : {
                            chevron: true,
                            render: (className, content) => (
                              <Link
                                to="/zakazky/$id"
                                params={{ id: row.caseId as string }}
                                className={className}
                                data-testid={`dnesek-happened-${row.id}`}
                              >
                                {content}
                              </Link>
                            ),
                          })}
                    />
                  ))}
                </V4Group>
              ) : (
                <p className="rh-a2-empty" data-testid="dnesek-happened-empty">
                  {t('webAdmin.dnesek.happenedEmpty')}
                </p>
              )}
            </>
          ) : null}

          {/* Krok CRM4: „Dnešní prohlídky" a „Nové shody poptávek" (nejvýš 3). */}
          {user !== null ? (
            <TodayViewings userId={user.id} from={todayStart.toISOString()} to={todayEnd.toISOString()} />
          ) : null}
          <NewDemandMatches />

          {/* ------------------------------------- „Zakázky v pohybu" */}
          <h2 className="rh-v4-h2" id="dnesek-pohyb-h">
            {casesInMotion.data?.scope === 'office'
              ? t('webAdmin.dnesek.casesMovingTitleOffice')
              : t('webAdmin.dnesek.summaryMotion')}
          </h2>
          {casesInMotion.data?.scope === 'office' ? (
            <p className="rh-a2-card__hint">{t('webAdmin.dnesek.casesMovingHintOffice')}</p>
          ) : null}
          {casesInMotion.isLoading ? (
            <p role="status" className="rh-a2-status">
              {t('webAdmin.dnesek.casesLoading')}
            </p>
          ) : null}
          {casesInMotion.error != null ? (
            <p role="alert" className="rh-a2-error" data-testid="dnesek-cases-error">
              {t('webAdmin.dnesek.casesLoadError')}
            </p>
          ) : null}
          {casesInMotion.data !== undefined ? (
            casesInMotion.data.items.length > 0 ? (
              <V4Group testId="dnesek-cases-list" label={t('webAdmin.dnesek.summaryMotion')}>
                {casesInMotion.data.items.map((item) => {
                  /*
                   * JEDNA funkce postupu (`lib/case-progress.ts`) — tytez
                   * procenta i veta „co chybí", jake ukazuje detail zakazky.
                   * „Chybí tvůj podpis" se pozna z toho, ze na zakazce lezi
                   * smlouva cekajici na podpis (data uz mame nahore).
                   */
                  const view = caseProgressView(item.stage, {
                    awaitingMySignature: signatureCaseIds.has(item.id),
                  });
                  return (
                    <V4Row
                      key={item.id}
                      icon={
                        <span className="rh-v4-thumb" aria-hidden="true">
                          <PropertyImage
                            photoUrl={item.coverUrl}
                            propertyId={item.propertyId}
                            lat={item.lat}
                            lon={item.lon}
                            size={{ width: 128, height: 128 }}
                            testIdPrefix={`dnesek-case-${item.id}`}
                          />
                        </span>
                      }
                      title={item.name}
                      sub={
                        <V4Progress view={view} testId={`dnesek-case-progress-${item.id}`} />
                      }
                      render={(className, content) => (
                        <Link
                          to="/zakazky/$id"
                          params={{ id: item.id }}
                          className={className}
                          data-testid={`dnesek-case-${item.id}`}
                          {...(item.nameHint !== undefined ? { title: item.nameHint } : {})}
                        >
                          {content}
                        </Link>
                      )}
                    />
                  );
                })}
              </V4Group>
            ) : (
              <p className="rh-a2-empty">{t('webAdmin.dnesek.casesMovingEmpty')}</p>
            )
          ) : null}
          <p className="rh-a2-card__foot">
            <Link to="/zakazky" className="rh-a2-link" data-testid="dnesek-all-cases-link">
              {t('webAdmin.dnesek.allCasesLink')}
            </Link>
          </p>

          {showBillingCard && billing.data !== undefined ? (
            <V4Group testId="dnesek-billing">
              <V4Row
                title={t('webAdmin.fakturace.title')}
                sub={t('webAdmin.fakturace.readiness.title', { percent: billing.data.readiness.percent })}
                chevron
                render={(className, content) => (
                  <Link to="/fakturace" className={className} data-testid="dnesek-billing-link">
                    {content}
                  </Link>
                )}
              />
            </V4Group>
          ) : null}
        </div>
      </div>
    </div>
  );
}
